<template>
  <div class="parent-theme planner-root">

    <!-- ── SIDEBAR ── -->
    <aside class="planner-sidebar">
      <!-- Logo -->
      <div class="planner-logo">
        <RouterLink to="/parent/dashboard" class="planner-logo-mark">
          <img src="/new-logo.png" class="planner-logo-icon" alt="">
          <div>
            <div class="planner-logo-text">Maths Eleven Plus</div>
            <div class="planner-logo-sub">Parent Portal</div>
          </div>
        </RouterLink>
      </div>

      <!-- Nav -->
      <nav class="planner-nav">
        <RouterLink to="/parent/dashboard" class="planner-nav-item" :class="{ active: isActive('/parent/dashboard') }">
          <span class="planner-nav-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </span>
          Dashboard
        </RouterLink>

        <RouterLink
          v-if="firstChildId"
          :to="`/parent/${firstChildId}/metrics`"
          class="planner-nav-item"
          :class="{ active: route.path.includes('/metrics') }"
        >
          <span class="planner-nav-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
          </span>
          Progress
        </RouterLink>
        <div v-else class="planner-nav-item planner-nav-disabled">
          <span class="planner-nav-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
          </span>
          Progress
        </div>

        <RouterLink
          to="/parent/subscription"
          class="planner-nav-item"
          :class="{ active: route.path.includes('/subscription') && !route.path.includes('/manage-account') }"
        >
          <span class="planner-nav-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
          </span>
          Subscription
          <span v-if="isFreePlan" class="planner-nav-upgrade">Upgrade</span>
        </RouterLink>

        <RouterLink
          to="/parent/manage-account"
          class="planner-nav-item"
          :class="{ active: route.path.includes('/manage-account') }"
        >
          <span class="planner-nav-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
          </span>
          Manage Account
        </RouterLink>
      </nav>

      <!-- Footer: Parent info + sign out -->
      <div class="planner-sidebar-footer">
        <div class="planner-parent-pill">
          <div class="planner-parent-avatar">{{ parentInitial }}</div>
          <div class="planner-parent-info">
            <div class="planner-parent-name">{{ parentName }}</div>
            <div class="planner-parent-plan">{{ planLabel }}</div>
          </div>
        </div>
        <button class="planner-signout-btn" @click="logout">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Sign out
        </button>
      </div>
    </aside>

    <!-- ── MOBILE TOP BAR ── -->
    <div class="planner-mobile-bar">
      <RouterLink to="/parent/dashboard" class="planner-mobile-logo">
        <img src="/new-logo.png" class="planner-logo-icon" style="width:28px;height:28px;" alt="">
        <span class="planner-logo-text" style="font-size:14px;">Maths Eleven Plus</span>
      </RouterLink>
      <button class="planner-mobile-menu-btn" @click="mobileOpen = !mobileOpen">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>
    </div>

    <!-- Mobile drawer -->
    <div v-if="mobileOpen" class="planner-mobile-drawer" @click="mobileOpen = false">
      <div class="planner-mobile-drawer-inner" @click.stop>
        <RouterLink to="/parent/dashboard" class="planner-mobile-nav-item" @click="mobileOpen = false">Dashboard</RouterLink>
        <button class="planner-mobile-nav-item planner-mobile-logout" @click="logout">Sign out</button>
      </div>
    </div>

    <!-- ── MAIN ── -->
    <main class="planner-main">
      <RouterView />
      <SiteFooter class="planner-footer" />
    </main>

    <FeedbackWidget />

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { useStudentStore } from '@/stores/studentStore';
import { useAuth } from '@/composables/useAuth';
import SiteFooter from '@/components/layout/SiteFooter.vue';
import FeedbackWidget from '@/components/layout/FeedbackWidget.vue';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const studentStore = useStudentStore();
const { logout: authLogout } = useAuth();

// Reload children on hard refresh — studentStore.students is empty after a
// page reload because it only gets populated after login/onboarding normally.
// Also refresh subscription from the API (syncAccount) so stale localStorage
// sessions are corrected (e.g. renewsAt passed, plan changed on another device).
// Running here rather than main.js avoids racing with the payment/welcome flow.
onMounted(() => {
  const uid = userStore.session?.uid;
  if (uid && studentStore.students.length === 0) {
    studentStore.loadChildren(uid);
  }
  userStore.syncAccount();
});

const mobileOpen = ref(false);

const firstChildId = computed(() => studentStore.students[0]?.id || null);
const effectivePlan = computed(() => userStore.effectivePlan || 'free');
const isFreePlan    = computed(() => effectivePlan.value === 'free');

const parentName = computed(() => userStore.session?.name || 'Parent');
const parentInitial = computed(() => parentName.value.charAt(0).toUpperCase());
const planLabel = computed(() => {
  return { free: 'Free Plan', pro: 'Pro Plan', pro_plus: 'Pro Plus Plan', family: 'Family Plan' }[effectivePlan.value] || 'Free Plan';
});

function isActive(path) {
  return route.path.startsWith(path);
}

async function logout() {
  await authLogout();
  studentStore.logout();
  router.push('/auth/login');
}
</script>

<style>
/* ── Layout shell ── */
.planner-root {
  display: flex;
  min-height: 100vh;
  background: #F5F4F0;
}

/* ── Sidebar ── */
.planner-sidebar {
  width: 240px;
  flex-shrink: 0;
  background: #1A2332;
  display: flex;
  flex-direction: column;
  padding: 28px 0;
  min-height: 100vh;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
}

.planner-logo {
  padding: 0 20px 28px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  margin-bottom: 12px;
}
.planner-logo-mark {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}
.planner-logo-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  flex-shrink: 0;
}
.planner-logo-text {
  font-size: 15px;
  font-weight: 800;
  color: white;
  line-height: 1.2;
}
.planner-logo-sub {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.planner-nav {
  flex: 1;
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.planner-nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  border-radius: 8px;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s;
  user-select: none;
}
.planner-nav-item:hover:not(.planner-nav-disabled) {
  background: rgba(255,255,255,0.05);
  color: #e2e8f0;
}
.planner-nav-item.active {
  background: rgba(245,158,11,0.12);
  color: #F59E0B;
}
.planner-nav-disabled {
  cursor: default;
  opacity: 0.45;
}
.planner-nav-icon {
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.planner-nav-soon {
  margin-left: auto;
  font-size: 10px;
  font-weight: 700;
  background: rgba(255,255,255,0.08);
  color: #475569;
  padding: 2px 7px;
  border-radius: 20px;
}
.planner-nav-upgrade {
  margin-left: auto;
  font-size: 10px;
  font-weight: 700;
  background: rgba(245,158,11,0.18);
  color: #F59E0B;
  padding: 2px 8px;
  border-radius: 20px;
}

.planner-sidebar-footer {
  margin-top: auto;
  padding: 20px;
  border-top: 1px solid rgba(255,255,255,0.08);
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.planner-parent-pill {
  display: flex;
  align-items: center;
  gap: 10px;
}
.planner-parent-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}
.planner-parent-info { overflow: hidden; }
.planner-parent-name {
  font-size: 15px;
  font-weight: 600;
  color: #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.planner-parent-plan {
  font-size: 13px;
  color: #94a3b8;
  margin-top: 2px;
}
.planner-signout-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.12);
  color: #94a3b8;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  width: 100%;
}
.planner-signout-btn:hover {
  background: rgba(255,255,255,0.05);
  color: #cbd5e1;
  border-color: rgba(255,255,255,0.18);
}

/* ── Main content area ── */
.planner-main {
  flex: 1;
  min-width: 0;
  padding: 32px 32px 0;
  overflow-y: auto;
}

/* ── Mobile top bar (hidden on desktop) ── */
.planner-mobile-bar {
  display: none;
}
.planner-mobile-drawer {
  display: none;
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .planner-root {
    flex-direction: column;
  }
  .planner-sidebar {
    display: none;
  }
  .planner-mobile-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #1A2332;
    padding: 14px 20px;
    position: sticky;
    top: 0;
    z-index: 50;
  }
  .planner-mobile-logo {
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
  }
  .planner-mobile-menu-btn {
    background: transparent;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 4px;
  }
  .planner-mobile-drawer {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 100;
  }
  .planner-mobile-drawer-inner {
    background: #1A2332;
    width: 240px;
    height: 100%;
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .planner-mobile-nav-item {
    display: block;
    padding: 12px 16px;
    border-radius: 8px;
    color: #94a3b8;
    font-size: 15px;
    font-weight: 500;
    text-decoration: none;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: all 0.15s;
  }
  .planner-mobile-nav-item:hover { background: rgba(255,255,255,0.06); color: #e2e8f0; }
  .planner-mobile-logout { color: #f87171; margin-top: 16px; }
  .planner-main {
    padding: 20px 16px 0;
  }
}
</style>
